import React, { useState, useEffect } from 'react';
import { useWallet } from '../hooks/useWallet';
import './Swap.css';

const REST = 'https://rest.amitis.network';
const RPC = 'https://rpc.amitis.network';
const DENOM = 'uamts';
const DECIMALS = 1_000_000;

export default function Swap() {
  const { wallet, balance, fetchBalance, client } = useWallet();
  const [pools, setPools] = useState([]);
  const [selectedPool, setSelectedPool] = useState(null);
  const [amountIn, setAmountIn] = useState('');
  const [direction, setDirection] = useState('amts_to_token');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPools();
    const interval = setInterval(fetchPools, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchPools = async () => {
    try {
      const res = await fetch(`${REST}/amitis/amitisdex/v1/pools`);
      const data = await res.json();
      setPools(data.pools || []);
      if (data.pools?.length > 0 && !selectedPool) {
        setSelectedPool(data.pools[0]);
      }
    } catch (e) {}
  };

  const estimateOutput = () => {
    if (!selectedPool || !amountIn || isNaN(amountIn) || parseFloat(amountIn) <= 0) return null;
    const input = parseFloat(amountIn) * DECIMALS;
    const feeRate = 0.003;
    const inputAfterFee = input * (1 - feeRate);

    let inReserve, outReserve;
    if (direction === 'amts_to_token') {
      inReserve = parseInt(selectedPool.amts_reserve || 0);
      outReserve = parseInt(selectedPool.token_reserve || 0);
    } else {
      inReserve = parseInt(selectedPool.token_reserve || 0);
      outReserve = parseInt(selectedPool.amts_reserve || 0);
    }

    if (!inReserve || !outReserve) return null;
    const output = (outReserve * inputAfterFee) / (inReserve + inputAfterFee);
    const fee = input * feeRate;
    const rebate = fee * 0.8;
    const effectiveFee = fee - rebate;

    return {
      output: output / DECIMALS,
      fee: fee / DECIMALS,
      rebate: rebate / DECIMALS,
      effectiveFee: effectiveFee / DECIMALS,
    };
  };

  const handleSwap = async () => {
    if (!wallet || !client || !selectedPool || !amountIn) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const amountInUamts = Math.floor(parseFloat(amountIn) * DECIMALS).toString();
      const estimate = estimateOutput();
      if (!estimate) throw new Error('Could not estimate output');

      // Minimum output with 1% slippage tolerance
      const minOut = Math.floor(estimate.output * DECIMALS * 0.99).toString();

      const msg = {
        typeUrl: '/amitis.amitisdex.MsgSwapExactIn',
        value: {
          sender: wallet.address,
          pool_id: selectedPool.id,
          amount_in: { denom: direction === 'amts_to_token' ? DENOM : selectedPool.token_denom, amount: amountInUamts },
          min_amount_out: minOut,
        }
      };

      const fee = { amount: [{ denom: DENOM, amount: '300000' }], gas: '500000' };
      const tx = await client.signAndBroadcast(wallet.address, [msg], fee);

      if (tx.code === 0) {
        setResult({ txhash: tx.transactionHash, output: estimate.output });
        setAmountIn('');
        await fetchBalance(wallet.address);
        await fetchPools();
      } else {
        setError(`Swap failed: ${tx.rawLog}`);
      }
    } catch (e) {
      setError(e.message || 'Swap failed');
    } finally {
      setLoading(false);
    }
  };

  const estimate = estimateOutput();

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-pill mono">DEX</div>
        <h1 className="page-title">
          Swap tokens.<br />
          <span className="italic green">Keep the fees.</span>
        </h1>
        <p className="page-sub">
          Trade on the native Amitis DEX. 80% of swap fees are returned to you automatically.
        </p>
      </div>

      {pools.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">💧</div>
          <h3>No pools yet</h3>
          <p>Liquidity pools are coming soon. Be a Founding LP and earn fees on every swap.</p>
        </div>
      ) : (
        <div className="swap-layout">
          <div className="swap-card">
            <div className="swap-header">
              <h3>Swap</h3>
              <button className="swap-settings">⚙</button>
            </div>

            <div className="token-input">
              <div className="token-input-header">
                <span className="mono muted">From</span>
                {wallet && <span className="mono muted">Balance: {balance?.toFixed(2)} AMTS</span>}
              </div>
              <div className="token-input-body">
                <input
                  type="number"
                  placeholder="0.00"
                  value={amountIn}
                  onChange={e => { setAmountIn(e.target.value); setResult(null); setError(null); }}
                  className="amount-input"
                />
                <button className="token-select">
                  {direction === 'amts_to_token' ? 'AMTS' : selectedPool?.token_denom?.toUpperCase() || 'TOKEN'}
                  <span>▼</span>
                </button>
              </div>
            </div>

            <button
              className="swap-flip"
              onClick={() => { setDirection(d => d === 'amts_to_token' ? 'token_to_amts' : 'amts_to_token'); setAmountIn(''); }}
            >
              ⇅
            </button>

            <div className="token-input">
              <div className="token-input-header">
                <span className="mono muted">To (estimated)</span>
              </div>
              <div className="token-input-body">
                <input
                  type="number"
                  placeholder="0.00"
                  value={estimate ? estimate.output.toFixed(6) : ''}
                  readOnly
                  className="amount-input"
                />
                <button className="token-select">
                  {direction === 'amts_to_token' ? selectedPool?.token_denom?.toUpperCase() || 'TOKEN' : 'AMTS'}
                  <span>▼</span>
                </button>
              </div>
            </div>

            {estimate && (
              <div className="fee-breakdown">
                <div className="fee-row">
                  <span className="mono muted">Swap fee (0.3%)</span>
                  <span>{estimate.fee.toFixed(6)} AMTS</span>
                </div>
                <div className="fee-row green-row">
                  <span className="mono green">80% rebate back to you</span>
                  <span className="green">+{estimate.rebate.toFixed(6)} AMTS</span>
                </div>
                <div className="fee-row">
                  <span className="mono muted">Effective fee</span>
                  <span>{estimate.effectiveFee.toFixed(6)} AMTS (0.06%)</span>
                </div>
              </div>
            )}

            <button
              className="swap-btn"
              disabled={!wallet || !amountIn || !estimate || loading}
              onClick={handleSwap}
            >
              {!wallet ? 'Connect Wallet to Swap' : loading ? 'Swapping...' : 'Swap'}
            </button>

            {error && <div className="claim-error"><span>⚠️</span> {error}</div>}
            {result && (
              <div className="claim-success">
                <div className="success-icon">✓</div>
                <div className="success-content">
                  <strong>Swap complete!</strong>
                  <span className="mono">tx: {result.txhash?.slice(0, 20)}...</span>
                  <p className="success-note">You received ~{result.output?.toFixed(4)} tokens. Check your balance for the fee rebate.</p>
                </div>
              </div>
            )}
          </div>

          {selectedPool && (
            <div className="pool-info">
              <h3>Pool Info</h3>
              <div className="pool-stats">
                <div className="pool-stat">
                  <span className="mono muted">AMTS Reserve</span>
                  <strong>{(parseInt(selectedPool.amts_reserve || 0) / DECIMALS).toLocaleString()} AMTS</strong>
                </div>
                <div className="pool-stat">
                  <span className="mono muted">Token Reserve</span>
                  <strong>{(parseInt(selectedPool.token_reserve || 0) / DECIMALS).toLocaleString()}</strong>
                </div>
                <div className="pool-stat">
                  <span className="mono muted">Fee Rate</span>
                  <strong>0.3% <span className="green">(0.06% effective)</span></strong>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
